const logoBlocks = document.querySelectorAll( '[data-samenwerk-sticky-logo="true"]' );

const svgDefinitionTags = new Set( [ 'defs', 'style', 'title', 'desc', 'metadata', 'clippath', 'mask', 'pattern', 'lineargradient', 'radialgradient' ] );

const isSvgDefinition = ( element ) => {
	let current = element;

	while ( current ) {
		if ( svgDefinitionTags.has( current.tagName?.toLowerCase() ) ) {
			return true;
		}

		current = current.parentElement;
	}

	return false;
};

const addGeneratedElementIds = ( svg ) => {
	if ( ! svg ) {
		return;
	}

	if ( ! svg.hasAttribute( 'id' ) ) {
		svg.setAttribute( 'id', 'samenwerk-logo-root' );
	}

};

const idsFromAttribute = ( value ) => ( value || '' )
	.split( ',' )
	.map( ( id ) => id.trim() )
	.filter( ( id ) => /^[A-Za-z][A-Za-z0-9_.:-]*$/.test( id ) );

const getConfiguredElement = ( logo, id ) => {
	const svg = logo.querySelector( 'svg.samenwerk-inline-logo' );

	if ( svg?.id === id ) {
		return svg;
	}

	const element = svg?.querySelector( `#${ CSS.escape( id ) }` );

	if ( element ) {
		return element;
	}

	const match = id.match( /^samenwerk-logo-element-(\d+)$/ );

	if ( ! svg || ! match ) {
		return null;
	}

	return [ ...svg.querySelectorAll( '*' ) ]
		.filter( ( candidate ) => ! isSvgDefinition( candidate ) && ! candidate.hasAttribute( 'id' ) )[ Number( match[ 1 ] ) ] || null;
};

const getStateIds = ( logo ) => {
	const header = logo.closest( 'header' );
	const fullIds = idsFromAttribute( logo.dataset.samenwerkStickyLogoFullIds );
	const compactIds = idsFromAttribute( logo.dataset.samenwerkStickyLogoCompactIds );

	if ( header?.classList.contains( 'is-sticky' ) ) {
		return compactIds;
	}

	return fullIds;
};

const fitViewBoxToState = ( logo ) => {
	const svg = logo.querySelector( 'svg.samenwerk-inline-logo' );
	const ids = getStateIds( logo );
	const originalViewBox = svg?.dataset.samenwerkOriginalViewBox;

	if ( ! svg ) {
		return;
	}

	if ( ids.length === 0 ) {
		if ( originalViewBox ) {
			svg.setAttribute( 'viewBox', originalViewBox );
		} else {
			svg.removeAttribute( 'viewBox' );
		}

		return;
	}

	if ( ids.some( ( id ) => getConfiguredElement( logo, id ) === svg ) ) {
		if ( originalViewBox ) {
			svg.setAttribute( 'viewBox', originalViewBox );
		}

		return;
	}

	const boxes = ids
		.map( ( id ) => getConfiguredElement( logo, id ) )
		.filter( ( element ) => element && ! isSvgDefinition( element ) )
		.map( ( element ) => {
			try {
				return element.getBBox( { stroke: true, markers: true } );
			} catch ( error ) {
				try {
					return element.getBBox();
				} catch ( fallbackError ) {
					return null;
				}
			}
		} )
		.filter( ( box ) => box && box.width > 0 && box.height > 0 );

	if ( boxes.length === 0 ) {
		return;
	}

	const bounds = boxes.reduce( ( result, box ) => ( {
		x: Math.min( result.x, box.x ),
		y: Math.min( result.y, box.y ),
		right: Math.max( result.right, box.x + box.width ),
		bottom: Math.max( result.bottom, box.y + box.height ),
	} ), {
		x: boxes[ 0 ].x,
		y: boxes[ 0 ].y,
		right: boxes[ 0 ].x + boxes[ 0 ].width,
		bottom: boxes[ 0 ].y + boxes[ 0 ].height,
	} );
	const padding = Math.max( 4, Math.min( 32, Math.max( bounds.right - bounds.x, bounds.bottom - bounds.y ) * 0.02 ) );

	svg.setAttribute( 'viewBox', `${ bounds.x - padding } ${ bounds.y - padding } ${ bounds.right - bounds.x + padding * 2 } ${ bounds.bottom - bounds.y + padding * 2 }` );
};

const setLogoState = ( logo ) => {
	const fullIds = idsFromAttribute( logo.dataset.samenwerkStickyLogoFullIds );
	const compactIds = idsFromAttribute( logo.dataset.samenwerkStickyLogoCompactIds );

	logo.classList.toggle( 'has-sticky-logo-full', fullIds.length > 0 );

	logo.querySelectorAll( '.is-sticky-logo-full, .is-sticky-logo-compact, .is-sticky-logo-full-hidden, .is-sticky-logo-compact-hidden' ).forEach( ( element ) => {
		element.classList.remove( 'is-sticky-logo-full', 'is-sticky-logo-compact', 'is-sticky-logo-full-hidden', 'is-sticky-logo-compact-hidden' );
	} );

	const setState = ( id, state ) => {
		const element = getConfiguredElement( logo, id );

		if ( ! element ) {
			return;
		}

		element.classList.add( `is-sticky-logo-${ state }` );

		[ ...( element.parentElement?.children || [] ) ].forEach( ( sibling ) => {
			if ( sibling === element || isSvgDefinition( sibling ) ) {
				return;
			}

			sibling.classList.add( `is-sticky-logo-${ state }-hidden` );
		} );
	};

	fullIds.forEach( ( id ) => {
		const element = getConfiguredElement( logo, id );

		if ( element ) {
			setState( id, 'full' );
		}
	} );
	compactIds.forEach( ( id ) => {
		const element = getConfiguredElement( logo, id );

		if ( element ) {
			setState( id, 'compact' );
		}
	} );

	fitViewBoxToState( logo );
};

const inlineLogo = async ( logo ) => {
	const image = logo.querySelector( 'img' );
	const source = image?.currentSrc || image?.src;

	if ( ! image || ! source ) {
		return;
	}

	try {
		const url = new URL( source, window.location.href );

		if ( 'https:' !== url.protocol || ! url.pathname.toLowerCase().endsWith( '.svg' ) ) {
			return;
		}

		const fetchUrl = url.origin === window.location.origin
			? url
			: new URL( `${ url.pathname }${ url.search }`, window.location.origin );
		const response = await fetch( fetchUrl, {
			credentials: 'same-origin',
		} );

		if ( ! response.ok ) {
			return;
		}

		const parsed = new DOMParser().parseFromString( await response.text(), 'image/svg+xml' );
		const svg = parsed.documentElement;

		if ( 'svg' !== svg.nodeName.toLowerCase() || parsed.querySelector( 'parsererror, script, foreignObject' ) ) {
			return;
		}

		svg.querySelectorAll( '*' ).forEach( ( element ) => {
			[ ...element.attributes ].forEach( ( attribute ) => {
				if ( attribute.name.toLowerCase().startsWith( 'on' ) ) {
					element.removeAttribute( attribute.name );
				}
			} );
		} );

		addGeneratedElementIds( svg );

		svg.classList.add( 'custom-logo', 'samenwerk-inline-logo' );
		svg.setAttribute( 'focusable', 'false' );

		if ( ! svg.hasAttribute( 'viewBox' ) ) {
			const width = Number.parseFloat( svg.getAttribute( 'width' ) );
			const height = Number.parseFloat( svg.getAttribute( 'height' ) );

			if ( width > 0 && height > 0 ) {
				svg.setAttribute( 'viewBox', `0 0 ${ width } ${ height }` );
			}
		}

		svg.dataset.samenwerkOriginalViewBox = svg.getAttribute( 'viewBox' ) || '';

		const imageWidth = image.getAttribute( 'width' );

		if ( imageWidth ) {
			svg.setAttribute( 'width', imageWidth );
		}

		svg.removeAttribute( 'height' );

		if ( image.alt ) {
			svg.setAttribute( 'role', 'img' );
			svg.setAttribute( 'aria-label', image.alt );
		}

		image.replaceWith( parsed.adoptNode( svg ) );
		addGeneratedElementIds( logo.querySelector( 'svg.samenwerk-inline-logo' ) );
		setLogoState( logo );

		const header = logo.closest( 'header' );

		if ( header ) {
			new MutationObserver( () => fitViewBoxToState( logo ) ).observe( header, {
				attributes: true,
				attributeFilter: [ 'class' ],
			} );
		}
	} catch ( error ) {
		// Keep the original Site Logo when the SVG cannot be safely inlined.
	}
};

logoBlocks.forEach( ( logo ) => {
	inlineLogo( logo );
} );
